import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  ArrowLeft,
  Moon,
  Star,
  Heart,
  Brain,
  Lightbulb,
  MessageCircle,
  Calendar,
  Eye,
  Tag,
  Pencil,
  Trash2,
  Share,
  Lock,
  Unlock,
  Sparkles,
  BarChart3,
  BookOpen,
  CheckCircle,
  XCircle
} from 'lucide-react';

const DreamDetail = () => {
  const { dreamId } = useParams();
  const navigate = useNavigate();
  const [dream, setDream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const [activeTab, setActiveTab] = useState('analysis');
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [generatingReflection, setGeneratingReflection] = useState(false);


  useEffect(() => {
    fetchDream();
  }, [dreamId]);

  // Remove automatic validation - only regenerate when user clicks button
  // useEffect(() => {
  //   if (dream && !loading && !hasValidatedDream) {
  //     // Clear any existing timeout
  //     if (validationTimeoutRef.current) {
  //       clearTimeout(validationTimeoutRef.current);
  //     }
  //     
  //     // Add a small delay to prevent rapid validation
  //     validationTimeoutRef.current = setTimeout(() => {
  //       validateAndRegenerateSymbols();
  //     }, 1000);
  //   }
  //   
  //   // Cleanup function to clear timeout
  //   return () => {
  //     if (validationTimeoutRef.current) {
  //       clearTimeout(validationTimeoutRef.current);
  //     }
  //   };
  // }, [dream, loading, hasValidatedDream]);

  const fetchDream = async () => {
    try {
      const response = await api.get(`/dreams/${dreamId}`);
      const data = response.data;
      setDream(data.dream);
      
      // Initialize reflection answers
      const answers = {};
      if (data.dream.reflectionPrompts) {
        data.dream.reflectionPrompts.forEach(prompt => {
          if (prompt.answered) {
            answers[prompt._id] = prompt.answer;
          }
        });
      }
      setReflectionAnswers(answers);
    } catch (error) {
      console.error('Error fetching dream:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReflectionAnswer = async (promptId, answer) => {
    try {
      const response = await api.post(`/dreams/${dreamId}/reflection/${promptId}`, { answer });

      if (response.status === 200) {
        setReflectionAnswers(prev => ({ ...prev, [promptId]: answer }));
        fetchDream(); // Refresh dream data
      }
    } catch (error) {
      console.error('Error saving reflection answer:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this dream? This action cannot be undone.')) {
      try {
        const response = await api.delete(`/dreams/${dreamId}`);

        if (response.status === 200) {
          navigate('/app/dream');
        }
      } catch (error) {
        console.error('Error deleting dream:', error);
      }
    }
  };

  const generateInsights = async (forceRegenerate = false) => {
    setGeneratingInsights(true);
    try {
      const response = await api.post(`/dreams/${dreamId}/insights`, { forceRegenerate });
      
      if (response.status === 200) {
        // Update the dream with new insights
        setDream(prevDream => ({
          ...prevDream,
          aiAnalysis: {
            ...prevDream.aiAnalysis,
            insights: response.data.insights,
            lastInsightUpdate: new Date().toISOString()
          }
        }));
        
        // Show notification if insights were cached
        if (response.data.cached) {
          console.log('Using cached insights');
        }
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setGeneratingInsights(false);
    }
  };

  const generateAIReflection = async () => {
    setGeneratingReflection(true);
    try {
      const response = await api.post(`/dreams/${dreamId}/ai-reflection`);
      
      if (response.status === 200) {
        // Update the dream with new AI reflection
        setDream(prevDream => ({
          ...prevDream,
          aiAnalysis: {
            ...prevDream.aiAnalysis,
            aiReflection: response.data.aiReflection,
            aiReflectionGeneratedAt: response.data.aiReflectionGeneratedAt
          }
        }));
      }
    } catch (error) {
      console.error('Error generating AI reflection:', error);
    } finally {
      setGeneratingReflection(false);
    }
  };





  const getEmotionColor = (emotion) => {
    const colors = {
      calm: 'text-blue-600 bg-blue-50',
      anxious: 'text-yellow-600 bg-yellow-50',
      excited: 'text-green-600 bg-green-50',
      sad: 'text-gray-600 bg-gray-50',
      angry: 'text-red-600 bg-red-50',
      confused: 'text-purple-600 bg-purple-50',
      refreshed: 'text-emerald-600 bg-emerald-50',
      neutral: 'text-gray-600 bg-gray-50',
      other: 'text-gray-600 bg-gray-50'
    };
    return colors[emotion] || colors.neutral;
  };

  const getDreamTypeColor = (type) => {
    const colors = {
      normal: 'text-blue-600 bg-blue-50',
      nightmare: 'text-red-600 bg-red-50',
      lucid: 'text-purple-600 bg-purple-50',
      recurring: 'text-orange-600 bg-orange-50',
      prophetic: 'text-indigo-600 bg-indigo-50',
      daydream: 'text-green-600 bg-green-50',
      other: 'text-gray-600 bg-gray-50'
    };
    return colors[type] || colors.normal;
  };

  const getMoodColor = (mood) => {
    const colors = {
      positive: 'text-green-600 bg-green-50',
      negative: 'text-red-600 bg-red-50',
      neutral: 'text-gray-600 bg-gray-50',
      mixed: 'text-purple-600 bg-purple-50'
    };
    return colors[mood] || colors.neutral;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dream...</p>
        </div>
      </div>
    );
  }

  if (!dream) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Moon className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Dream not found</h3>
          <p className="text-gray-600">The dream you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/app/dream')}
            className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Dreams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/app/dream')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3">
                              <div className="p-2 bg-purple-100 rounded-lg">
                <Moon className="h-6 w-6 text-purple-600" />
              </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {dream.title || 'Untitled Dream'}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {new Date(dream.dreamDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setEditing(!editing)}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Pencil className="h-4 w-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white/60 backdrop-blur-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'analysis', label: 'AI Analysis', icon: Sparkles },
              { id: 'reflection', label: 'Reflection', icon: MessageCircle },
              { id: 'insights', label: 'Insights', icon: Lightbulb },
              { id: 'details', label: 'Details', icon: BookOpen }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dream Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Dream Content</h2>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDreamTypeColor(dream.dreamType)}`}>
                {dream.dreamType}
              </span>
              {dream.isRecurring && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-600">
                  Recurring
                </span>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
              {dream.content}
            </p>
          </div>

          {/* Dream Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 text-gray-600 mb-1">
                <Eye className="h-4 w-4" />
                <span className="text-sm">Vividness</span>
              </div>
              <div className="flex items-center justify-center space-x-1">
                {[...Array(10)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < dream.vividness ? 'text-yellow-400 fill-current' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 text-gray-600 mb-1">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm">Lucidity</span>
              </div>
              <div className="flex items-center justify-center space-x-1">
                {[...Array(10)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < dream.lucidity ? 'text-purple-400 fill-current' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="text-center">
              <div className="text-gray-600 text-sm mb-1">Before Sleep</div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionColor(dream.emotionBeforeSleep)}`}>
                {dream.emotionBeforeSleep}
              </span>
            </div>

            <div className="text-center">
              <div className="text-gray-600 text-sm mb-1">After Waking</div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionColor(dream.emotionAfterWaking)}`}>
                {dream.emotionAfterWaking}
              </span>
            </div>
          </div>

          {/* Tags */}
          {dream.tags && dream.tags.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-3">
                <Tag className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {dream.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'analysis' && dream.aiAnalysis && (
          <div className="space-y-8">
            {/* Overall Analysis */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
                                <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Analysis</h2>
                  </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Heart className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-gray-900">Overall Mood</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMoodColor(dream.aiAnalysis.overallMood)}`}>
                    {dream.aiAnalysis.overallMood}
                  </span>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Brain className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-900">Stress Level</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(dream.aiAnalysis.stressLevel / 10) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-600">{dream.aiAnalysis.stressLevel}/10</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-gray-900">Analysis Confidence</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">
                    {Math.round(dream.aiAnalysis.symbols?.[0]?.confidence * 100 || 85)}%
                  </span>
                </div>
              </div>

              {/* Basic Symbol Detection */}
              {dream.aiAnalysis.symbols && dream.aiAnalysis.symbols.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Detected Symbols</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dream.aiAnalysis.symbols.map((symbol, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 capitalize">{symbol.symbol}</h4>
                          <span className="text-xs text-gray-500">
                            {Math.round(symbol.confidence * 100)}% confidence
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{symbol.meaning}</p>
                        {symbol.personalContext && (
                          <p className="text-xs text-purple-600 bg-purple-50 p-2 rounded">
                            {symbol.personalContext}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Emotions */}
              {dream.aiAnalysis.emotions && dream.aiAnalysis.emotions.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Emotional Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dream.aiAnalysis.emotions.map((emotion, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 capitalize">{emotion.emotion}</h4>
                          <div className="flex items-center space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-2 h-2 rounded-full ${
                                  i < emotion.intensity ? 'bg-red-400' : 'bg-gray-300'
                                }`}
                              ></div>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{emotion.context}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Themes */}
              {dream.aiAnalysis.themes && dream.aiAnalysis.themes.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Dream Themes</h3>
                  <div className="space-y-4">
                    {dream.aiAnalysis.themes.map((theme, index) => (
                      <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                        <h4 className="font-medium text-gray-900 mb-2">{theme.theme}</h4>
                        <p className="text-sm text-gray-600 mb-2">{theme.description}</p>
                        <p className="text-sm text-purple-700 font-medium">{theme.significance}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}


            </div>
          </div>
        )}

        {activeTab === 'reflection' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">AI Reflection</h2>
              </div>
              {(!dream.aiAnalysis?.aiReflection || dream.aiAnalysis.aiReflection.length === 0) && (
                <button
                  onClick={generateAIReflection}
                  disabled={generatingReflection}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingReflection ? (
                    <>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate AI Reflection</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {dream.aiAnalysis?.aiReflection ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-2 bg-blue-200 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                      AI Reflection
                    </span>
                  </div>
                  
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {dream.aiAnalysis.aiReflection}
                    </p>
                  </div>
                  
                  {dream.aiAnalysis.aiReflectionGeneratedAt && (
                    <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-blue-200">
                      AI reflection generated on {new Date(dream.aiAnalysis.aiReflectionGeneratedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <MessageCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No AI reflection available</h3>
                <p className="text-gray-600">AI will study your dream and provide a thoughtful reflection on its meaning and significance</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'insights' && dream.aiAnalysis && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              {/* Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Lightbulb className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Therapeutic Insights</h2>
              </div>
              
              {/* Buttons and Status - Mobile Responsive */}
              {dream.aiAnalysis.insights && dream.aiAnalysis.insights.length > 0 ? (
                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => generateInsights(true)}
                    disabled={generatingInsights}
                    className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-xl font-medium hover:from-orange-600 hover:to-red-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingInsights ? (
                      <>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span>Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Regenerate Insights</span>
                      </>
                    )}
                  </button>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Insights Available
                    </span>
                    {dream.aiAnalysis?.lastInsightUpdate && new Date(dream.updatedAt) > new Date(dream.aiAnalysis.lastInsightUpdate) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Dream Updated
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => generateInsights(false)}
                  disabled={generatingInsights}
                  className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingInsights ? (
                    <>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate AI Insights</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {dream.aiAnalysis.insights && dream.aiAnalysis.insights.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dream.aiAnalysis.insights.map((insight, index) => (
                  <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-1 bg-green-200 rounded">
                        <Lightbulb className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-green-600 uppercase tracking-wide">
                        {insight.category}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{insight.insight}</h3>
                    {insight.actionable && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Actionable
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Lightbulb className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No insights available</h3>
                <p className="text-gray-600">AI analysis will generate therapeutic insights for your dream</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center space-x-3 mb-6">
                              <div className="p-2 bg-gray-100 rounded-lg">
                  <BookOpen className="h-6 w-6 text-gray-600" />
                </div>
              <h2 className="text-2xl font-bold text-gray-900">Dream Details</h2>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Dream Date</label>
                  <p className="text-gray-900">{new Date(dream.dreamDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Logged On</label>
                  <p className="text-gray-900">{new Date(dream.logTime).toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Dream Type</label>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getDreamTypeColor(dream.dreamType)}`}>
                    {dream.dreamType}
                  </span>
                </div>
                {dream.isRecurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Recurring Pattern</label>
                    <p className="text-gray-900">
                      {dream.recurringPattern?.frequency} - {dream.recurringPattern?.totalOccurrences} occurrences
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DreamDetail;
